import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Phone, Menu, X, MapPin, Share2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BRAND, telLink } from "@/lib/brand";
import ShareDialog from "@/components/ShareDialog";

const MAP_URL = BRAND.mapsUrl;

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const nav = [
    { to: "/", label: "Home" },
    { to: "/#services", label: "Services" },
    { to: "/#plans", label: "Plans" },
    { to: "/#reviews", label: "Reviews" },
    { to: "/#how", label: "How it Works" },
    { to: "/#faq", label: "FAQ" },
    { to: "/contact", label: "Contact" },
  ];

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-40 border-b transition-[background,backdrop-filter,border] duration-300 ${
        scrolled
          ? "bg-black/70 backdrop-blur-xl border-white/10"
          : "bg-black/40 backdrop-blur-md border-white/5"
      }`}
    >
      <div className="mx-auto max-w-7xl px-5 lg:px-8 h-20 flex items-center justify-between">
        <Link to="/" data-testid="brand-link" className="flex items-center gap-3">
          <div className="h-11 w-11 sm:h-12 sm:w-12 rounded-lg bg-white p-1.5 flex items-center justify-center shadow-[0_0_20px_rgba(0,229,255,0.25)] ring-1 ring-brand-cyan/40">
            <img src={BRAND.logoUrl} alt="Secure Watch logo" className="h-full w-full object-contain" />
          </div>
          <div className="leading-tight">
            <div className="font-heading font-extrabold tracking-tight text-white text-lg sm:text-xl">
              SECURE WATCH
            </div>
            <div className="text-[10px] sm:text-[11px] uppercase tracking-[0.22em] font-bold text-brand-cyan">
              Smart Security · Guwahati · Nagaon
            </div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {nav.map((n) => (
            <a
              key={n.to}
              href={n.to}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
              className={`text-sm text-white/70 hover:text-white transition-colors ${
                pathname === n.to ? "text-white" : ""
              }`}
            >
              {n.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setShareOpen(true)}
            data-testid="header-share-btn"
            aria-label="Share this website"
            className="hidden sm:inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/15 bg-white/5 text-white hover:text-brand-cyan hover:border-brand-cyan/50 transition-colors"
            title="Share Secure Watch"
          >
            <Share2 className="h-4 w-4" />
          </button>
          <a
            href={MAP_URL}
            target="_blank"
            rel="noreferrer noopener"
            data-testid="header-location-btn"
            aria-label="Open our location in Google Maps"
            className="hidden sm:inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/15 bg-white/5 text-white hover:text-brand-cyan hover:border-brand-cyan/50 transition-colors"
            title="Our Guwahati office"
          >
            <MapPin className="h-4 w-4" />
          </a>
          <a href={telLink} data-testid="header-call-btn" className="hidden sm:inline-flex">
            <Button className="bg-brand-cyan text-black hover:bg-brand-cyan/90 hover:-translate-y-0.5 transition-transform font-semibold shadow-[0_0_20px_rgba(0,229,255,0.35)]">
              <Phone className="mr-2 h-4 w-4" />
              Call Now
            </Button>
          </a>
          <button
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-toggle"
            className="lg:hidden p-2 text-white"
            aria-label="Toggle menu"
          >
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-white/10 bg-black/90 backdrop-blur-xl">
          <div className="mx-auto max-w-7xl px-5 py-4 flex flex-col gap-3">
            {nav.map((n) => (
              <a
                key={n.to}
                href={n.to}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                className="text-white/80 py-2 border-b border-white/5"
              >
                {n.label}
              </a>
            ))}
            <a href={telLink} data-testid="mobile-call-btn">
              <Button className="w-full bg-brand-cyan text-black hover:bg-brand-cyan/90 font-semibold">
                <Phone className="mr-2 h-4 w-4" />
                Call {BRAND.phoneDisplay}
              </Button>
            </a>
            <a href={MAP_URL} target="_blank" rel="noreferrer noopener" data-testid="mobile-location-btn">
              <Button variant="outline" className="w-full border-white/20 text-white hover:bg-white/10 font-semibold">
                <MapPin className="mr-2 h-4 w-4 text-brand-cyan" />
                Our Guwahati office
              </Button>
            </a>
            <button
              onClick={() => { setOpen(false); setShareOpen(true); }}
              data-testid="mobile-share-btn"
              className="w-full inline-flex items-center justify-center gap-2 rounded-md border border-brand-cyan/40 bg-brand-cyan/10 text-brand-cyan h-10 font-semibold text-sm"
            >
              <Share2 className="h-4 w-4" />
              Share this website
            </button>
          </div>
        </div>
      )}

      <ShareDialog open={shareOpen} onOpenChange={setShareOpen} />
    </header>
  );
}
